import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation, Route, Switch } from 'react-router-dom';
import { Box, Snackbar, Alert, Tooltip } from '@mui/material';
import historyIcon from '../../assets/icons/history.svg';
import editIcon from '../../assets/icons/edit.svg';
import CircularIconButton from '../../components/ui/CircularIconButton';
import { useAuth } from '../../context/AuthContext';
import { transactionAPI, caisseAPI } from '../../services/api';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import TransactionDetails from './TransactionDetails';
import TransactionHistory from './TransactionHistory';
import TransactionsContent from './TransactionsContent';
import TransactionEditModal from './TransactionEditModal';
import TransactionRefsEditModal from './TransactionRefsEditModal';
import ConfirmVersementModal from './ConfirmVersementModal';
import StatusDropdownPill from './StatusDropdownPill';
import checkIcon from '../../assets/icons/check.svg';

const Transactions = () => {
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    total: 0
  });
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  
  // Caisse dropdown state
  const [selectedCaisseIds, setSelectedCaisseIds] = useState([]);
  const [caisseOptions, setCaisseOptions] = useState([]);
  const [physiqueCaisses, setPhysiqueCaisses] = useState([]);
  
  // Success/Error feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit modal state
  const [editModal, setEditModal] = useState({
    open: false,
    transaction: null,
    descriptionOnly: false
  });
  const [refsEditModal, setRefsEditModal] = useState({
    open: false,
    transaction: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [transactionRefreshKey, setTransactionRefreshKey] = useState(0);
  
  // Confirm versement modal state
  const [confirmVersementModal, setConfirmVersementModal] = useState({ open: false, transaction: null });

  // AbortController ref for cancelling in-flight requests
  const abortControllerRef = useRef(null);

  // Handle edit transaction
  const handleEditTransaction = (transaction) => {
    setEditModal({
      open: true,
      transaction: transaction
    });
  };

  const handleEditDescriptionOnly = (transaction) => {
    setEditModal({ open: true, transaction, descriptionOnly: true });
  };

  const handleCloseEditModal = () => {
    setEditModal({ open: false, transaction: null, descriptionOnly: false });
  };

  const handleEditTransactionRefs = (transaction) => {
    setRefsEditModal({ open: true, transaction });
  };

  const handleUpdateTransactionRefs = (transactionId, refs) => {
    setTransactions(prev => prev.map(t =>
      t.id === transactionId ? { ...t, references: refs } : t
    ));
    setTransactionRefreshKey(prev => prev + 1);
    setRefsEditModal({ open: false, transaction: null });
  };

  const handleUpdateTransaction = async (transactionId, newDescription, updatedRefs) => {
    setIsEditing(true);
    try {
      setTransactions(prev => prev.map(transaction => {
        if (transaction.id !== transactionId) return transaction;
        const updates = { description: newDescription };
        if (updatedRefs !== undefined) updates.references = updatedRefs;
        return { ...transaction, ...updates };
      }));
      setTransactionRefreshKey(prev => prev + 1);
      setEditModal({ open: false, transaction: null, descriptionOnly: false });
    } catch (error) {
      console.error('Error updating transaction:', error);
      setError('Erreur lors de la mise à jour de la transaction');
    } finally {
      setIsEditing(false);
    }
  };

  // Handle status change for verification agents
  const handleStatusChange = async (transactionId, newStatus) => {
    try {
      // Determine verification type based on user role
      let verificationType = null;
      if (user?.role === 'POINTAGE_CLIENT') {
        verificationType = 'CLIENT';
      } else if (user?.role === 'POINTAGE_COMPTABILITE') {
        verificationType = 'COMPTABILITE';
      }
      // Legacy VERIFICATION role removed - all users migrated to POINTAGE_CLIENT or POINTAGE_COMPTABILITE
      
      await transactionAPI.updateVerificationStatus(transactionId, newStatus, verificationType);
      await fetchTransactions(); // Refresh transactions to show updated status
      
      // Show success message after refresh
      const typeLabel = verificationType === 'CLIENT' ? 'client' : verificationType === 'COMPTABILITE' ? 'comptabilité' : '';
      setSnackbar({ 
        open: true, 
        message: `Statut de vérification ${typeLabel ? typeLabel + ' ' : ''}modifié avec succès`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error updating transaction status:', error);
      setError('Erreur lors de la mise à jour du statut');
    }
  };

        // Filter state
        const [filters, setFilters] = useState({
          searchTerm: '',
          categoryDetailIds: [],
          caisseIds: [],
          userIds: [],
          amountMin: '',
          amountMax: '',
          dateRange: null,
          requiresPointage: null,
          verificationStatus: null,
        });
        
        // Sorting state
        const [sortBy, setSortBy] = useState('createdAt');
        const [sortDirection, setSortDirection] = useState('DESC');

        // Fetch transactions with AbortController support
        const fetchTransactions = useCallback(async () => {
          // Cancel any in-flight request
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          
          // Create new AbortController for this request
          const abortController = new AbortController();
          abortControllerRef.current = abortController;
          
          setLoading(true);
          setError(null);
          
          try {
            const params = {
              page: pagination.page,
              size: pagination.size,
              sortBy: sortBy,
              sortDirection: sortDirection,
              searchTerm: filters.searchTerm,
              ...(filters.requiresPointage && filters.requiresPointage !== '' && filters.requiresPointage !== 'client' && filters.requiresPointage !== 'comptabilite' && { requiresPointage: filters.requiresPointage === 'true' }),
              ...(filters.requiresPointage === 'client' && { requiresPointageClient: true }),
              ...(filters.requiresPointage === 'comptabilite' && { requiresPointageComptabilite: true }),
              // Convert date range to backend format (avoid timezone issues)
              fromDate: filters.dateRange?.start ? filters.dateRange.start.toLocaleDateString('en-CA') : undefined,
              toDate: filters.dateRange?.end ? filters.dateRange.end.toLocaleDateString('en-CA') : undefined,
            };

            // Add multi-select parameters - axios will handle array serialization
            if (filters.categoryDetailIds && filters.categoryDetailIds.length > 0) {
              params.categoryDetailIds = filters.categoryDetailIds;
            }
            
            if (filters.caisseIds && filters.caisseIds.length > 0) {
              params.caisseIds = filters.caisseIds;
            }
            
            if (filters.userIds && filters.userIds.length > 0) {
              params.userIds = filters.userIds;
            }

            // Add verification status filter for verification agents
            if (filters.verificationStatus) {
              params.verificationStatus = filters.verificationStatus;
            }

            // Remove empty values
            Object.keys(params).forEach(key => {
              if (params[key] === '' || params[key] === null || params[key] === undefined || 
                  (Array.isArray(params[key]) && params[key].length === 0)) {
                delete params[key];
              }
            });

            const response = await transactionAPI.getAll(params, {
              signal: abortController.signal
            });
            
            // Check if request was aborted
            if (abortController.signal.aborted) {
              return;
            }
            
            setTransactions(response.data.content || []);
            setPagination(prev => ({
              ...prev,
              total: response.data.totalElements || 0
            }));
          } catch (err) {
            // Don't set error if request was aborted
            if (err.name === 'AbortError' || abortController.signal.aborted) {
              return;
            }
            console.error('Error fetching transactions:', err);
            setError('Échec du chargement des transactions');
          } finally {
            // Only update loading state if this request wasn't aborted
            if (!abortController.signal.aborted) {
              setLoading(false);
            }
          }
        }, [filters, pagination.page, pagination.size, sortBy, sortDirection]);

  // Initialize filters from URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryDetailIds = searchParams.get('categoryDetailIds');
    const caisseIds = searchParams.get('caisseIds');
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');
    
    if (categoryDetailIds || caisseIds || fromDate || toDate) {
      setFilters(prev => ({
        ...prev,
        ...(categoryDetailIds && {
          categoryDetailIds: categoryDetailIds.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id))
        }),
        ...(caisseIds && {
          caisseIds: caisseIds.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id))
        }),
        ...(fromDate && toDate && {
          dateRange: {
            start: new Date(fromDate),
            end: new Date(toDate)
          }
        })
      }));
      
      // Update selectedCaisseIds if caisseIds are in URL
      if (caisseIds) {
        const ids = caisseIds.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
        if (ids.length > 0) {
          setSelectedCaisseIds(ids);
        }
      }
    }
  }, [location.search]);

  // Load caisse options on mount
  useEffect(() => {
    const loadCaisseOptions = async () => {
      try {
        // Check if caisseIds are already in URL - if so, don't set default
        const searchParams = new URLSearchParams(location.search);
        const urlCaisseIds = searchParams.get('caisseIds');
        const hasUrlCaisseIds = urlCaisseIds && urlCaisseIds.trim().length > 0;
        
        if (user?.role === 'MANAGER') {
          const response = await caisseAPI.getForFilters();
          const caisses = Array.isArray(response.data) ? response.data : [];
          
          const physiques = caisses
            .filter(c => c.type === 'PHYSIQUE')
            .map(caisse => ({ id: caisse.id, name: caisse.name, type: 'PHYSIQUE' }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          const mobiles = caisses
            .filter(c => c.type === 'MOBILE')
            .map(caisse => ({ id: caisse.id, name: caisse.name, type: 'MOBILE' }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          const allCaisses = [...physiques, ...mobiles];
          setCaisseOptions(allCaisses);
          setPhysiqueCaisses(physiques);
          
          // Only set default if no caisseIds in URL and selectedCaisseIds is empty
          if (!hasUrlCaisseIds && selectedCaisseIds.length === 0) {
            const firstPhysique = physiques.length > 0 ? physiques[0] : (allCaisses.length > 0 ? allCaisses[0] : null);
            if (firstPhysique) {
              setSelectedCaisseIds([firstPhysique.id]);
            }
          }
        } else if (user?.role === 'POINTAGE_CLIENT' || user?.role === 'POINTAGE_COMPTABILITE') {
          const assignedCaisses = user?.assignedCaisses || [];
          
          const physiques = assignedCaisses
            .filter(c => c.type === 'PHYSIQUE')
            .map(caisse => ({ id: caisse.id, name: caisse.name, type: 'PHYSIQUE' }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          const mobiles = assignedCaisses
            .filter(c => c.type === 'MOBILE')
            .map(caisse => ({ id: caisse.id, name: caisse.name, type: 'MOBILE' }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          const allCaisses = [...physiques, ...mobiles];
          setCaisseOptions(allCaisses);
          setPhysiqueCaisses(physiques);
          
          // Only set default if no caisseIds in URL and selectedCaisseIds is empty
          if (!hasUrlCaisseIds && selectedCaisseIds.length === 0) {
            const firstPhysique = physiques.length > 0 ? physiques[0] : (allCaisses.length > 0 ? allCaisses[0] : null);
            if (firstPhysique) {
              setSelectedCaisseIds([firstPhysique.id]);
            }
          }
        } else if (user?.role === 'AGENT') {
          const assignedCaisse = user?.assignedCaisses?.[0];
          if (assignedCaisse) {
            const option = { id: assignedCaisse.id, name: assignedCaisse.name, type: assignedCaisse.type };
            setCaisseOptions([option]);
            // Only set default if no caisseIds in URL
            if (!hasUrlCaisseIds) {
              setSelectedCaisseIds([assignedCaisse.id]);
            }
          }
        }
      } catch (error) {
        console.error('Error loading caisse options:', error);
      }
    };

    if (user) {
      loadCaisseOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.search]);

  // Handle caisse change: clear transactions, reset pagination, update filters, and fetch
  useEffect(() => {
    // Skip if no caisse selected (for managers/verification/pointage)
    if ((user?.role === 'MANAGER' || user?.role === 'POINTAGE_CLIENT' || user?.role === 'POINTAGE_COMPTABILITE') && selectedCaisseIds.length === 0) {
      setTransactions([]);
      return;
    }

    // Clear transactions immediately when caisse changes
    setTransactions([]);
    setLoading(true);
    
    // Reset pagination to page 0 when caisse changes
    setPagination(prev => ({
      ...prev,
      page: 0,
      total: 0
    }));
    
    // Update filters with new caisse IDs
    setFilters(prev => ({
      ...prev,
      caisseIds: selectedCaisseIds
    }));
  }, [selectedCaisseIds, user?.role]);

  // Load transactions when filters or pagination change
  // This runs after filters are updated from selectedCaisseIds change
  useEffect(() => {
    // Skip if no caisse selected (for managers/verification/pointage)
    if ((user?.role === 'MANAGER' || user?.role === 'POINTAGE_CLIENT' || user?.role === 'POINTAGE_COMPTABILITE') && selectedCaisseIds.length === 0) {
      return;
    }
    
    // Only fetch if filters are properly set (for managers/verification/pointage, ensure caisseIds is set)
    if ((user?.role === 'MANAGER' || user?.role === 'POINTAGE_CLIENT' || user?.role === 'POINTAGE_COMPTABILITE') && 
        (!filters.caisseIds || filters.caisseIds.length === 0)) {
      return;
    }
    
    // Debounce: small delay to prevent rapid successive calls
    const timeoutId = setTimeout(() => {
      fetchTransactions();
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [fetchTransactions, user?.role, selectedCaisseIds, filters.caisseIds, filters.searchTerm, filters.categoryDetailIds, filters.userIds, filters.dateRange, filters.requiresPointage, filters.verificationStatus, pagination.page, pagination.size, sortBy, sortDirection]);

  // Sorting handler
  const handleSort = (field, direction) => {
    setSortBy(field);
    setSortDirection(direction);
    // Reset to first page when sorting changes
    setPagination(prev => ({ ...prev, page: 0 }));
  };

        // Handle filter changes
        const handleFiltersChange = (newFilters) => {
          setFilters(newFilters);
          setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page
          setSelectedTransactions([]); // Clear selections when filters change
        };

        // Handle reset filters
        const handleResetFilters = () => {
          setFilters({
            searchTerm: '',
            categoryDetailIds: [],
            caisseIds: selectedCaisseIds.length > 0 ? selectedCaisseIds : [],
            userIds: [],
            amountMin: '',
            amountMax: '',
            dateRange: null,
            requiresPointage: null,
            verificationStatus: null,
          });
          // Reset caisse to first physical caisse for managers/verification/pointage
          if ((user?.role === 'MANAGER' || user?.role === 'POINTAGE_CLIENT' || user?.role === 'POINTAGE_COMPTABILITE')) {
            const firstPhysique = physiqueCaisses.length > 0 ? physiqueCaisses[0] : (caisseOptions.length > 0 ? caisseOptions[0] : null);
            if (firstPhysique) {
              setSelectedCaisseIds([firstPhysique.id]);
            }
          }
          setPagination(prev => ({ ...prev, page: 0 }));
        };

        // Check if any filters are active
        const hasActiveFilters = useCallback(() => {
          return filters.searchTerm?.trim() || 
                 filters.categoryDetailIds?.length > 0 || 
                 filters.caisseIds?.length > 0 || 
                 filters.userIds?.length > 0 ||
                 filters.dateRange?.start || 
                 filters.dateRange?.end ||
                 filters.requiresPointage !== null ||
                 filters.verificationStatus != null
        }, [filters]);

        // Handle export
        const handleExport = useCallback(async (type) => {
          try {
            
            // Check if export should be disabled
            const hasFilters = hasActiveFilters();
            const hasSelection = selectedTransactions.length > 0;
            
            if (!hasFilters && !hasSelection) {
              return;
            }
            
            const params = {};

            // Priority 1: If specific transactions are selected, export only those
            if (hasSelection) {
              // Get transaction codes from the selected transaction IDs
              const selectedTransactionCodes = selectedTransactions.map(id => {
                const transaction = transactions.find(t => t.id === id);
                return transaction ? transaction.code : null;
              }).filter(code => code !== null);
              
              if (selectedTransactionCodes.length > 0) {
                params.transactionCodes = selectedTransactionCodes;
              }
            } 
            // Priority 2: If filters are active, export all filtered data (not just current page)
            else if (hasFilters) {
              params.searchTerm = filters.searchTerm;
              if (filters.requiresPointage && filters.requiresPointage !== '' && filters.requiresPointage !== 'client' && filters.requiresPointage !== 'comptabilite') {
                params.requiresPointage = filters.requiresPointage === 'true';
              } else if (filters.requiresPointage === 'client') {
                params.requiresPointageClient = true;
              } else if (filters.requiresPointage === 'comptabilite') {
                params.requiresPointageComptabilite = true;
              }
              params.fromDate = filters.dateRange?.start ? filters.dateRange.start.toLocaleDateString('en-CA') : undefined;
              params.toDate = filters.dateRange?.end ? filters.dateRange.end.toLocaleDateString('en-CA') : undefined;

              // Add multi-select parameters - axios will handle array serialization
              if (filters.categoryDetailIds && filters.categoryDetailIds.length > 0) {
                params.categoryDetailIds = filters.categoryDetailIds;
              }
              
              if (filters.caisseIds && filters.caisseIds.length > 0) {
                params.caisseIds = filters.caisseIds;
              }
              
              if (filters.userIds && filters.userIds.length > 0) {
                params.userIds = filters.userIds;
              }

              if (filters.verificationStatus && filters.verificationStatus !== '') {
                params.verificationStatus = filters.verificationStatus;
              }
            }

            // Remove empty values
            Object.keys(params).forEach(key => {
              if (params[key] === '' || params[key] === null || params[key] === undefined || 
                  (Array.isArray(params[key]) && params[key].length === 0)) {
                delete params[key];
              }
            });

            const statusForFilename = filters.verificationStatus && filters.verificationStatus !== '' ? filters.verificationStatus : null;

            if (type.toLowerCase() === 'csv') {
              const response = await transactionAPI.exportCSV(params);
              const filename = getFilenameFromResponse(response, 'csv', statusForFilename);
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', filename);
              document.body.appendChild(link);
              link.click();
              link.remove();
            } else if (type.toLowerCase() === 'pdf') {
              const response = await transactionAPI.exportPDF(params);
              const filename = getFilenameFromResponse(response, 'pdf', statusForFilename);
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', filename);
              document.body.appendChild(link);
              link.click();
              link.remove();
            }
    } catch (err) {
      console.error('Error exporting transactions:', err);
      setError('Échec de l\'exportation des transactions');
    }
  }, [filters, selectedTransactions, transactions, hasActiveFilters]);

  // Helper function to extract filename from response headers
  const getFilenameFromResponse = (response, defaultExtension, statusFilterValue) => {
    const formatStatusForFilename = (statusValue) => {
      if (!statusValue) return '';
      const statusMap = {
        OPEN: 'ouvert',
        '': '',
        IN_PROGRESS: 'en-cours-de-pointage',
        VERIFIED: 'verifie',
        en_cours_de_verification: 'en-cours-de-pointage',
        verifie: 'verifie',
        ouvert: 'ouvert'
      };
      const mapped = statusMap[statusValue] || statusValue;
      return mapped
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-_]/g, '');
    };

    const appendStatusToFilename = (baseName) => {
      const formattedStatus = formatStatusForFilename(statusFilterValue);
      if (!formattedStatus) return baseName;
      const lastDotIndex = baseName.lastIndexOf('.');
      if (lastDotIndex === -1) {
        return `${baseName}_statut-${formattedStatus}`;
      }
      const namePart = baseName.slice(0, lastDotIndex);
      const extensionPart = baseName.slice(lastDotIndex);
      return `${namePart}_statut-${formattedStatus}${extensionPart}`;
    };

    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        return appendStatusToFilename(filenameMatch[1]);
      }
    }
    // Fallback to default filename with timestamp
    const now = new Date();
    const timestamp = now.getFullYear() + '-' + 
      String(now.getMonth() + 1).padStart(2, '0') + '-' + 
      String(now.getDate()).padStart(2, '0') + '_' + 
      String(now.getHours()).padStart(2, '0') + '-' + 
      String(now.getMinutes()).padStart(2, '0') + '-' + 
      String(now.getSeconds()).padStart(2, '0');
    return appendStatusToFilename(`transactions_${timestamp}.${defaultExtension}`);
  };

  // Transform transaction data for Rows component
  const transformTransactionData = (transaction) => {
    // Determine amount color and sign based on factors stored at transaction creation
    // Factors are stored directly on TransactionDTO at creation time
    const caisseFactor = transaction.caisseFactor !== undefined ? transaction.caisseFactor : 0;
    const coffreFactor = transaction.coffreFactor !== undefined ? transaction.coffreFactor : 0;
    const baseAmount = Number(transaction.amount) || 0;
    let amountDisplay = '';
    let amountColor = '#027BC6'; // Default blue
    
    // Check if it's neutral: both factors are 0, OR both factors are non-zero (mixed)
    const isNeutral = (caisseFactor === 0 && coffreFactor === 0);
    const isMixed = (caisseFactor !== 0 && coffreFactor !== 0);
    
    if (isNeutral) {
      // Neutral - show raw amount, blue color
      amountDisplay = formatAmountForDisplay(baseAmount, false).text;
      amountColor = '#027BC6';
    } else if (isMixed) {
      // Mixed (e.g. prélèvement) - show as negative, red color
      const adjustedAmount = -Math.abs(baseAmount);
      amountDisplay = formatAmountForDisplay(adjustedAmount, true).text;
      amountColor = '#EF456E';
    } else {
      // Pure positive or negative
      const factor = caisseFactor !== 0 ? caisseFactor : coffreFactor;
      const isPositive = factor > 0;
      const adjustedAmount = baseAmount * factor;
      amountDisplay = formatAmountForDisplay(adjustedAmount, true).text;
      amountColor = isPositive ? '#0AD69F' : '#EF456E';
    }

    return {
      id: transaction.id,
      code: transaction.code,
      createdAt: transaction.createdAt, // Keep original for sorting
      date: transaction.createdAt ? new Date(transaction.createdAt).toLocaleString('fr-FR') : '', // For display
      category: transaction.category?.label || '',
      amount: amountDisplay,
      amountColor,
      Caisse: transaction.caisse?.name || '', // Match backend field name
      caisse: transaction.caisse?.name || '', // Keep for display compatibility
      createdBy: transaction.createdBy ? `${transaction.createdBy.firstName} ${transaction.createdBy.lastName}` : '', // Match backend field name
      user: transaction.createdBy ? `${transaction.createdBy.firstName} ${transaction.createdBy.lastName}` : '', // Keep for display compatibility
      description: transaction.description || '',
      // verificationStatus: transaction.verificationStatus || 'OPEN', // Legacy - commented out after migration
      verificationStatusClient: transaction.verificationStatusClient || 'OPEN', // Client pointage status
      verificationStatusComptabilite: transaction.verificationStatusComptabilite || 'OPEN', // Comptabilité pointage status
      isVersementCoffre: coffreFactor > 0 && transaction.caisse?.type === 'MOBILE',
      amountVerified: transaction.amountVerified || false,
      caisseAfter: transaction.caisseAfter ?? null,
      // Store original data for details modal
      originalData: transaction
    };
  };

  // Define columns for Rows component
  const getColumnsForRole = (userRole) => {
    const baseColumns = [
      { key: 'code', label: 'ID Transaction', sortable: true, width: '12%' },
      { 
        key: 'createdAt', 
        label: 'Date', 
        sortable: true,
        width: '14%',
        render: (createdAt) => {
          if (!createdAt) return '';
          const date = new Date(createdAt);
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const year = date.getFullYear();
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          const seconds = date.getSeconds().toString().padStart(2, '0');
          return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
        }
      },
      {
        key: 'category',
        label: 'Catégorie',
        sortable: true,
        width: (userRole === 'POINTAGE_CLIENT' || userRole === 'POINTAGE_COMPTABILITE') ? '10%' : '12%',
        render: (category, row) => {
          const bcRef = row.originalData?.references?.find(r => r.type === 'BC');
          if (!bcRef) return category;
          return (
            <span>
              {category}
              <span style={{ color: '#333333', fontWeight: 600 }}>{' - '}</span><span style={{ color: '#027BC6', fontWeight: 600 }}>{'BC N°'}{bcRef.value}</span>
            </span>
          );
        }
      },
      { 
        key: 'amount', 
        label: 'Montant', 
        sortable: true,
        width: (userRole === 'POINTAGE_CLIENT' || userRole === 'POINTAGE_COMPTABILITE') ? '12%' : '13%',
        render: (amount, row) => (
          <span style={{ 
            color: row.amountColor,
            fontFamily: 'Montserrat',
            fontSize: '16px',
            fontWeight: 'bold',
            lineHeight: '19px',
            letterSpacing: '0px',
            textTransform: 'none',
            opacity: 1
          }}>
            {amount}
          </span>
        )
      },
      { key: 'Caisse', label: 'Caisse', sortable: true, width: '12%' },
      { key: 'createdBy', label: 'Utilisateur', sortable: true, width: '12%' },
      { 
        key: 'description', 
        label: 'Description', 
        sortable: false, 
        width: '23%',
        render: (description) => {
          if (!description || description.trim() === '') {
            return <span style={{ color: '#919191', fontStyle: 'italic' }}>Aucune description</span>;
          }
          
          return (
            <Tooltip
              title={
                <Box
                  sx={{
                    color: '#FFFFFF',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '23px',
                    letterSpacing: '0px',
                    textTransform: 'none',
                    opacity: 1,
                    maxWidth: '279px',
                    wordWrap: 'break-word'
                  }}
                >
                  {description}
                </Box>
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#000000',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    '& .MuiTooltip-arrow': {
                      color: '#000000'
                    }
                  }
                },
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                }
              }}
              arrow
              placement="top"
              PopperProps={{
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                  {
                    name: 'flip',
                    options: {
                      fallbackPlacements: ['top', 'bottom'],
                    },
                  },
                ],
              }}
            >
              <span
                style={{
                  cursor: 'pointer',
                  color: '#333',
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  maxWidth: '100%',
                  textAlign: 'left'
                }}
              >
                {description}
              </span>
            </Tooltip>
          );
        }
      },
    ];

    // Show status column for pointage roles
    if (userRole === 'POINTAGE_CLIENT' || userRole === 'POINTAGE_COMPTABILITE') {
      // Determine which status to show based on user role
      const getStatusForRole = (row) => {
        if (userRole === 'POINTAGE_CLIENT') {
          return row.verificationStatusClient || 'OPEN';
        } else if (userRole === 'POINTAGE_COMPTABILITE') {
          return row.verificationStatusComptabilite || 'OPEN';
        }
      };

      baseColumns.push({
        key: 'verificationStatus',
        label: 'Statut',
        sortable: true,
        width: '10%',
        render: (status, row) => {
          const displayStatus = getStatusForRole(row);
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', margin: 0 }} onClick={(e) => e.stopPropagation()}>
              <StatusDropdownPill
                currentStatus={displayStatus}
                onStatusChange={(newStatus) => handleStatusChange(row.id, newStatus)}
              />
            </Box>
          );
        }
      });
    }

    // Add actions column only for non-pointage users
    if (userRole !== 'POINTAGE_CLIENT' && userRole !== 'POINTAGE_COMPTABILITE') {
      baseColumns.push({
        key: 'actions',
        label: '',
        sortable: false,
        width: '90px',
        render: (value, row) => {
          const tooltipProps = {
            arrow: true,
            placement: 'top',
            componentsProps: {
              tooltip: {
                sx: {
                  backgroundColor: '#000000',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  textTransform: 'none',
                },
              },
              arrow: { sx: { color: '#000000' } },
            },
          };

          if (user?.role === 'ADMIN' || user?.role === 'MANAGER') {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px', width: '100%' }} onClick={e => e.stopPropagation()}>
                {row.isVersementCoffre && (
                  row.amountVerified ? (
                    <CircularIconButton
                      icon={checkIcon}
                      alt="Confirmé"
                      iconSize={20}
                      sx={{
                        pointerEvents: 'none',
                        cursor: 'default',
                        '& img': {
                          filter: 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)',
                        },
                      }}
                    />
                  ) : (
                    <Tooltip title="Confirmer le montant" {...tooltipProps}>
                      <span>
                        <CircularIconButton
                          icon={checkIcon}
                          alt="Confirmer le montant"
                          tone="highlighted"
                          iconSize={20}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmVersementModal({ open: true, transaction: row.originalData });
                          }}
                        />
                      </span>
                    </Tooltip>
                  )
                )}
                <Tooltip title="Voir l'historique" {...tooltipProps}>
                  <span>
                    <CircularIconButton
                      icon={historyIcon}
                      alt="Historique"
                      aria-label="history"
                      tone="highlighted"
                      tintIcon={false}
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(`/transactions/${row.id}/history`);
                      }}
                    />
                  </span>
                </Tooltip>
              </Box>
            );
          } else if (user?.role === 'AGENT') {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularIconButton
                  icon={editIcon}
                  alt="Edit"
                  aria-label="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditTransaction(row.originalData);
                  }}
                />
              </Box>
            );
          }
          return null;
        }
      });
    }

    return baseColumns;
  };

  const columns = getColumnsForRole(user?.role);


  const transformedTransactions = transactions.map(transformTransactionData);


  return (
    <Box>
      <Switch>
        <Route exact path="/transactions">
          <Box className="pb-8">
            <TransactionsContent
            transactions={transactions}
            loading={loading}
            error={error}
            setError={setError}
            filters={filters}
            handleFiltersChange={handleFiltersChange}
            handleExport={handleExport}
            handleResetFilters={handleResetFilters}
            hasActiveFilters={hasActiveFilters}
            selectedTransactions={selectedTransactions}
            setSelectedTransactions={setSelectedTransactions}
            pagination={pagination}
            setPagination={setPagination}
            columns={columns}
            transformedTransactions={transformedTransactions}
            sortBy={sortBy}
            sortDirection={sortDirection}
            handleSort={handleSort}
            selectedCaisseIds={selectedCaisseIds}
            setSelectedCaisseIds={setSelectedCaisseIds}
            caisseOptions={caisseOptions}
          />
          </Box>
        </Route>
        
        <Route path="/transactions/:id/history">
          <Box className="pb-8">
            <TransactionsContent
              transactions={transactions}
              loading={loading}
              error={error}
              setError={setError}
              filters={filters}
              handleFiltersChange={handleFiltersChange}
              handleExport={handleExport}
              handleResetFilters={handleResetFilters}
              hasActiveFilters={hasActiveFilters}
              selectedTransactions={selectedTransactions}
              setSelectedTransactions={setSelectedTransactions}
              pagination={pagination}
              setPagination={setPagination}
              columns={columns}
              transformedTransactions={transformedTransactions}
              sortBy={sortBy}
              sortDirection={sortDirection}
              handleSort={handleSort}
              selectedCaisseIds={selectedCaisseIds}
              setSelectedCaisseIds={setSelectedCaisseIds}
              caisseOptions={caisseOptions}
            />
          </Box>
          {/* Transaction History Overlay */}
          <TransactionHistory />
        </Route>

        <Route path="/transactions/:id">
          <Box className="pb-8">
            <TransactionsContent
              transactions={transactions}
              loading={loading}
              error={error}
              setError={setError}
              filters={filters}
              handleFiltersChange={handleFiltersChange}
              handleExport={handleExport}
              handleResetFilters={handleResetFilters}
              hasActiveFilters={hasActiveFilters}
              selectedTransactions={selectedTransactions}
              setSelectedTransactions={setSelectedTransactions}
              pagination={pagination}
              setPagination={setPagination}
              columns={columns}
              transformedTransactions={transformedTransactions}
              sortBy={sortBy}
              sortDirection={sortDirection}
              handleSort={handleSort}
              selectedCaisseIds={selectedCaisseIds}
              setSelectedCaisseIds={setSelectedCaisseIds}
              caisseOptions={caisseOptions}
            />
          </Box>
          {/* Transaction Details Overlay */}
          <TransactionDetails onEdit={handleEditDescriptionOnly} onEditRefs={handleEditTransactionRefs} refreshKey={transactionRefreshKey} />
        </Route>
      </Switch>
      
      {/* Transaction Edit Modal - Outside Route */}
      <TransactionEditModal
        open={editModal.open}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateTransaction}
        loading={isEditing}
        transaction={editModal.transaction}
        descriptionOnly={editModal.descriptionOnly}
      />

      <TransactionRefsEditModal
        open={refsEditModal.open}
        onClose={() => setRefsEditModal({ open: false, transaction: null })}
        onSubmit={handleUpdateTransactionRefs}
        transaction={refsEditModal.transaction}
      />

      <ConfirmVersementModal
        open={confirmVersementModal.open}
        onClose={() => setConfirmVersementModal({ open: false, transaction: null })}
        transaction={confirmVersementModal.transaction}
        onConfirmed={() => {
          setTransactions(prev => prev.map(t =>
            t.id === confirmVersementModal.transaction?.id ? { ...t, amountVerified: true } : t
          ));
        }}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 2000 }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Transactions;

